import {useBoardLabels} from '@github-ui/board-core'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'

import {BoardWindowSpacer} from '../window/BoardWindow'
import cellStyles from './BoardCellFrame.module.css'
import columnStyles from './BoardColumnFrame.module.css'
import styles from './BoardSkeletons.module.css'

/** How many placeholder cards to draw when the caller states no page size. */
const FALLBACK_PAGE_SIZE = 3

export type BoardCardSkeletonProps = {
  /** Defaults to a varied width, re-rolled on remount. State one where instances churn. */
  width?: string
  className?: string
}

/**
 * One placeholder card.
 *
 * Carries a test id because it has no role or name to be found by: the bars are
 * decorative and the card is hidden from assistive technology, so a test that checks a
 * cell drew a page-shaped skeleton has nothing semantic to locate.
 */
export function BoardCardSkeleton({width = 'random', className}: BoardCardSkeletonProps) {
  return (
    <div className={clsx(styles.CardSkeleton, className)} data-testid="board-card-skeleton" aria-hidden>
      <LoadingSkeleton height="sm" width={width} variant="rounded" />
      <LoadingSkeleton height="sm" width="40%" variant="rounded" />
    </div>
  )
}

export type BoardCardSkeletonsProps = {
  /** `pageSize` from the board's data. A page arrives whole, so the skeleton is page-shaped. */
  count?: number
  className?: string
}

/**
 * A page of placeholder cards.
 *
 * As many placeholders as the caller asks for, so a page of cards that is loaded and
 * returned whole can be drawn as the transition from N placeholders to N cards.
 *
 * The board's own cell asks for one. A placeholder reserves the room its card lands in,
 * and a page-shaped reservation inside a cell leaves the reader exactly where they were
 * when the page arrives -- the scrollable area barely moves, so the board feels unchanged
 * by the thing it just did. A caller composing its own cell can still size this by
 * `pageSize`, which is what that field is for.
 *
 * The label is a real text node rather than an `aria-label` on the container. The bars
 * are decorative and hidden, so a container named by an attribute would carry a name
 * with no content behind it and be dropped from the reading order entirely.
 */
export function BoardCardSkeletons({count, className}: BoardCardSkeletonsProps) {
  const labels = useBoardLabels()
  const total = count ?? FALLBACK_PAGE_SIZE

  return (
    <div className={clsx(styles.CardSkeletons, className)}>
      <VisuallyHidden>{labels.loadingItems}</VisuallyHidden>
      {Array.from({length: total}, (_, index) => (
        <BoardCardSkeleton key={index} />
      ))}
    </div>
  )
}

export type BoardItemsLoadingRowProps = {
  /** For a cell holding cards nobody has fetched. */
  still?: boolean
  className?: string
}

/**
 * One cell's "a page is on its way": a single card, drawn as a placeholder.
 *
 * One rather than a page of them, and shaped like a card rather than reduced to a bar. A
 * bar at the end of a list of cards says something is happening without saying what is
 * coming; a card says a card is coming, which is the honest description of the next thing
 * to arrive.
 *
 * **Exactly as tall as the room `BoardPaging.module.css` keeps for it**, which is what
 * lets it appear without moving the reader a pixel: a cell that has more holds a card's
 * worth of room open at the end of its cards at all times, so arriving here is a swap
 * rather than a growth, and it is already on screen for a reader who has reached the end.
 *
 * The two heights are a contract, and a silent one, because nothing in CSS can state it.
 * `shows the reader a page is on its way, without moving them to do it` asserts the cell's
 * extent does not change across the swap, so drifting them apart fails a test rather than
 * quietly restoring the fault this was built to fix.
 *
 * The label is a real text node rather than an `aria-label` on the container, for the
 * reason `BoardCardSkeletons` gives.
 */
export function BoardItemsLoadingRow({still = false, className}: BoardItemsLoadingRowProps) {
  const labels = useBoardLabels()

  return (
    <div
      className={clsx(styles.ItemsLoadingRow, still && styles.Still, className)}
      data-testid={still ? 'board-items-unloaded' : 'board-items-loading'}
    >
      {still ? null : <VisuallyHidden>{labels.loadingItems}</VisuallyHidden>}
      <BoardCardSkeleton />
    </div>
  )
}

export function BoardCellSkeleton() {
  return (
    <div className={cellStyles.CellFrame} aria-hidden>
      <div className={cellStyles.Body}>
        <BoardCardSkeleton width="70%" />
      </div>
    </div>
  )
}

/** Only the heading: the sticky strip cannot hold anything taller. `Board` draws the body. */
export function BoardColumnSkeleton() {
  const labels = useBoardLabels()

  return (
    <div className={clsx(columnStyles.ColumnFrame, styles.ColumnSkeleton)}>
      <VisuallyHidden>{labels.loadingColumns}</VisuallyHidden>
      <LoadingSkeleton height="md" width="8rem" variant="rounded" aria-hidden />
    </div>
  )
}

export type BoardRowSkeletonProps = {
  columns?: number
  /** Without these the strip starts at the board's first column, not the first on screen. */
  leadingSize?: number | null
  trailingSize?: number | null
  className?: string
}

export function BoardRowSkeleton({columns = 1, leadingSize, trailingSize, className}: BoardRowSkeletonProps) {
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.RowSkeleton, className)}>
      <VisuallyHidden>{labels.loadingRows}</VisuallyHidden>
      <div className={styles.RowSkeletonHeader} aria-hidden>
        <LoadingSkeleton height="sm" width="8rem" variant="rounded" />
      </div>
      <div className={styles.RowSkeletonCells}>
        <BoardWindowSpacer axis="inline" size={leadingSize ?? null} />
        {Array.from({length: Math.max(columns, 1)}, (_, index) => (
          <BoardCellSkeleton key={index} />
        ))}
        <BoardWindowSpacer axis="inline" size={trailingSize ?? null} />
      </div>
    </div>
  )
}
